import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowRight, MapPinned, Search, HandshakeIcon, Sprout, LineChart, Layers, Sparkles, BadgeCheck } from 'lucide-react';
import ListingCard from '@/components/ListingCard';
import MapView from '@/components/MapView';
import SpotHeroVideo from '@/components/SpotHeroVideo';
import FeaturesCarousel from '@/components/FeaturesCarousel';
import Mascot from '@/components/Mascot';
import YieldAiBanner from '@/components/YieldAiBanner';
import Hero3D from '@/components/three/Hero3D';
import { HOME } from '@/constants/testIds';

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ users: 0, listings: 0, hectares: 0 });
  const nav = useNavigate();

  useEffect(() => {
    api.get('/listings', { params: { limit: 6, sort: 'recent' } }).then(r => setFeatured(r.data.items || [])).catch(() => {});
    api.get('/stats/public').then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* ========== HERO 3D — Globo Italia realistico ========== */}
      <Hero3D />

      {/* ========== STATS GLASS ========== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-10 relative z-20">
        <div className="liquid-glass liquid-glass-sheen noise-overlay rounded-3xl grid grid-cols-3 overflow-hidden">
          {[
            { k: stats.listings, l: 'Terreni pubblicati' },
            { k: stats.users, l: 'Utenti registrati' },
            { k: Math.round(stats.hectares || 0), l: 'Ettari totali', suffix: ' ha' },
          ].map((s, i) => (
            <div key={i} className={`p-6 md:p-10 relative ${i>0?'border-l border-white/5':''}`}>
              <div className="font-display text-3xl md:text-5xl tracking-tighter">
                {s.k}<span className="text-xl md:text-2xl text-chartreuse" style={{color:'rgb(180,255,0)'}}>{s.suffix||''}</span>
              </div>
              <div className="mt-2 text-xs md:text-sm text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES CAROUSEL — illustrazioni scorrevoli di tutto ciò che puoi fare */}
      <FeaturesCarousel />

      {/* SPOT PROMO — Video cinematografico */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Scopri AgriNexus</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">La terra italiana, in un ecosistema.</h2>
          </div>
          <Link to="/scopri-agrinexus" className="text-sm font-medium link-underline">Guarda lo spot completo →</Link>
        </div>
        <SpotHeroVideo aspectClass="aspect-[21/9]"/>
      </section>

      {/* MAP PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Anteprima mappa</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">Ogni terreno, dove serve.</h2>
          </div>
          <Link to="/mappa" className="text-sm font-medium link-underline">Apri la mappa completa →</Link>
        </div>
        <MapView listings={featured} height="480px" />
      </section>

      {/* YIELD AI PRO banner */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <YieldAiBanner variant="hero"/>
      </section>

      {/* HOW IT WORKS — glass cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Come funziona</div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight max-w-2xl">Tre passi. Zero attriti.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6 stagger">
          {[
            { n: '01', t: 'Cerca', d: 'Filtra terreni per zona, superficie, prezzo e caratteristiche. La mappa mostra ciò che davvero esiste.', I: Search },
            { n: '02', t: 'Trova', d: 'Ogni annuncio ha una scheda completa: foto, mappa, caratteristiche e stato di verifica.', I: MapPinned },
            { n: '03', t: 'Connettiti', d: 'Contatta il proprietario in chat real-time. I tuoi dati restano privati.', I: HandshakeIcon },
          ].map((s) => (
            <div key={s.n} className="relative liquid-glass liquid-glass-hover liquid-glass-sheen rounded-2xl p-8 overflow-hidden">
              <div className="absolute top-6 right-6 font-display text-5xl tracking-tighter text-white/5">{s.n}</div>
              <s.I size={28} strokeWidth={1.4} className="text-chartreuse" style={{color:'rgb(180,255,0)'}}/>
              <h3 className="mt-6 font-display text-2xl">{s.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Ultimi annunci</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight">Il territorio, in tempo reale.</h2>
            </div>
            <Link to="/esplora" className="text-sm font-medium link-underline">Esplora tutti →</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map(l => <ListingCard key={l.listing_id} listing={l} />)}
          </div>
        </section>
      )}

      {/* MATCHING AI section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <div className="relative rounded-3xl liquid-glass liquid-glass-sheen noise-overlay p-10 md:p-20 overflow-hidden">
          <div aria-hidden className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-chartreuse/15 blur-[100px] animate-pulse-glow"/>
          <div className="relative z-10 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-chartreuse mb-4" style={{color:'rgb(180,255,0)'}}>Matching intelligente</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
              Trova il terreno<br/>giusto per te.
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl">
              Indica cosa cerchi — zona, budget, superficie, colture. AgriNexus calcola un punteggio di compatibilità trasparente per ogni terreno. Zero magia nera, solo dati verificabili.
            </p>
            <div className="mt-8 flex gap-3">
              <Button className="rounded-full chartreuse-glow" onClick={() => nav('/registrati')}>Imposta preferenze</Button>
              <Button variant="outline" className="rounded-full liquid-glass" onClick={() => nav('/come-funziona')}>Come funziona →</Button>
            </div>
          </div>
        </div>
      </section>

      {/* AI assistant panel — mascot entrance */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <div className="relative rounded-3xl liquid-glass liquid-glass-sheen noise-overlay p-10 md:p-16 overflow-hidden">
          <div className="grid md:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-1.5" style={{color:'rgb(180,255,0)'}}>
                <Sparkles size={12}/> Powered by AI
              </div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-[1.05]">
                Non sei da solo.<br/>
                <span className="italic font-light">Giulio ti guida.</span>
              </h2>
              <p className="mt-6 text-muted-foreground max-w-lg">
                Il nostro assistente AI (Claude Sonnet 4.6, streaming real-time) risponde a domande su colture, normative italiane e valutazioni. Analizza automaticamente ogni terreno e ti aiuta a scrivere annunci professionali in secondi.
              </p>
              <div className="mt-8 flex gap-3 flex-wrap">
                <Button className="rounded-full h-11 px-6 chartreuse-glow" onClick={() => nav('/assistente')} data-testid="home-ai-cta">
                  <Sparkles size={14} className="mr-2"/> Prova l'assistente AI
                </Button>
                <Button variant="outline" className="rounded-full h-11 px-6 liquid-glass" onClick={() => nav('/come-funziona')}>
                  Scopri di più
                </Button>
              </div>
              <div className="mt-4 text-[10px] text-muted-foreground italic">
                Le informazioni AI sono supporto, non sostituiscono un agronomo qualificato.
              </div>
            </div>
            <div className="hidden md:flex flex-col items-center shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-chartreuse/20 rounded-full blur-3xl animate-pulse-glow"/>
                <Mascot pose="wave" size={240}/>
              </div>
              <div className="mt-2 liquid-glass text-foreground text-xs px-3 py-1.5 rounded-full font-medium">Ciao, sono Giulio 👋</div>
            </div>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM pillars */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Un nuovo modo di vivere l'agricoltura</div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight max-w-3xl">Un ecosistema che cresce con te.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { I: LineChart, t: 'Dati reali', d: 'Nessun numero inventato. Statistiche, terreni e utenti provengono dal database in tempo reale.' },
            { I: Layers, t: 'Architettura scalabile', d: 'Progettato oggi per accogliere professionisti, macchinari, finanziamenti e marketplace agricolo di domani.' },
            { I: Sprout, t: 'AI attiva', d: 'Assistente conversazionale, Land Intelligence sui terreni e descrizioni professionali — già disponibili. Sempre come supporto, mai come sostituzione.' },
          ].map((s,i) => (
            <div key={i} className="liquid-glass liquid-glass-hover liquid-glass-sheen rounded-2xl p-8">
              <s.I size={28} strokeWidth={1.4} className="text-chartreuse" style={{color:'rgb(180,255,0)'}}/>
              <h3 className="mt-6 font-display text-2xl">{s.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-32 mb-8">
        <div className="relative rounded-3xl liquid-glass liquid-glass-sheen noise-overlay p-10 md:p-20 overflow-hidden text-center">
          <div aria-hidden className="absolute inset-0 hero-radial pointer-events-none"/>
          <div className="relative z-10">
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
              Comincia oggi.<br/>
              <span className="italic font-light text-chartreuse" style={{color:'rgb(180,255,0)'}}>È gratis.</span>
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-muted-foreground">
              Zero costi per pubblicare, cercare o contattare. Beta aperta a tutti, in attesa che l'ecosistema cresca.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap justify-center">
              <Button size="lg" className="rounded-full h-12 px-7 chartreuse-glow" onClick={() => nav('/registrati')}>
                Crea un account <ArrowRight className="ml-2" size={16}/>
              </Button>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7 liquid-glass" onClick={() => nav('/esplora')}>
                Oppure esplora prima
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
